import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { TabView, TabPanel } from "primereact/tabview";
import { InputSwitch } from "primereact/inputswitch";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import Helpers from "../../utils/helpers";
import OrderService from "../../services/order/order.service";
import { selectedGetway } from "../../redux/slices/getway-link";
import RouteConstant from "../../constants/route.constants";
import { itemCount } from "../../redux/slices/counter";
import { selectedPayment } from "../../redux/slices/payments";
import NewOrderReview from "../New-Invoice-Report/new-order-review";
import OrderPrint from "../New-Invoice-Report/new-order-print";
import orderServices from "../../services/orders/orderservice";
import TokenService from "../../services/autentication/token.service";
import AuthService from "../../services/autentication/auth.service";

interface SidecartSummaryProps {
    onBack: () => void;
    onNext?: () => void;
    onClose: () => void;
    onOrderComplete?: () => void;
    onChangeAddress: () => void;
    onChangeDeliveryMethod: () => void;
    onChangePaymentMethod: () => void;
}

const SidecartSummary: React.FC<SidecartSummaryProps> = ({
    onBack,
    onNext,
    onClose,
    onOrderComplete,
    onChangeAddress,
    onChangeDeliveryMethod,
    onChangePaymentMethod
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [orderId, setOrderId] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successDetails, setSuccessDetails] = useState({
        amount: 0,
        date: "",
        deliveryDate: ""
    });
    const [visibleOrderDetail, setVisibleOrderDetail] = useState(false);
    const [detailsOrder, setDetailsOrder] = useState<any>(null);

    // Reward points
    const [availableRewardPoints, setAvailableRewardPoints] = useState(0);
    const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState(0);
    const [useRewards, setUseRewards] = useState(true);
    const [rewardsPointsRedeemed, setRewardsPointsRedeemed] = useState(0);

    const selectedAddress = useAppSelector(state => state.userAddress.selecedUserAddresses);
    const deliveryMethod = useAppSelector(state => state.deliveryMethod.selectedDeliveryMethod);
    const isAdmin = useAppSelector(state => state.isAdmin);
    const comment = useAppSelector(state => state.comment);
    const adminUsersId = useAppSelector(state => state.adminUser.selectedAdminUserId);

    useEffect(() => {
        const processed = ProductStorageHelpers.GetProcessedProductStorage();
        if (processed) {
            const courierCharges = Number(JSON.parse(localStorage.getItem("courier-charges") || "0"));
            const grandTotal = Math.round(processed.totalItemCost + processed.totalTax + courierCharges);
            setData({ ...processed, courierCharges, grandTotal });
        }
    }, []);

    // Fetch reward points
    useEffect(() => {
        const fetchRewards = async () => {
            try {
                let userId: number | null = null;
                if (isAdmin && adminUsersId !== 0) {
                    userId = adminUsersId;
                } else if (AuthService.isUserLoggedIn()) {
                    const userDetails = TokenService.getUser();
                    userId = userDetails?.userID || null;
                }
                if (!userId) return;
                const res = await OrderService.getUserRewardBalance(userId);
                const points = res?.availablePoints ?? 0;
                const ratio = res?.rewardToCurrencyRatio ?? 0;
                setAvailableRewardPoints(points);
                setRewardToCurrencyRatio(ratio);
                if (points <= 0) setUseRewards(false);
            } catch {
                setUseRewards(false);
            }
        };
        fetchRewards();
    }, [isAdmin, adminUsersId]);

    // Compute redeeming points whenever toggle/totals change
    useEffect(() => {
        if (useRewards && rewardToCurrencyRatio > 0 && data) {
            const orderTotal = data.grandTotal;
            const pointsNeeded = Math.floor(orderTotal / rewardToCurrencyRatio);
            setRewardsPointsRedeemed(Math.min(availableRewardPoints, pointsNeeded));
        } else {
            setRewardsPointsRedeemed(0);
        }
    }, [useRewards, availableRewardPoints, rewardToCurrencyRatio, data]);

    const showMixedCartError = React.useMemo(() => {
        if (!data || !data.items || data.items.length === 0) return false;
        const allSupportCourier = data.items.every((item: any) => item.courierAvailable === true);
        const allSupportHomeDelivery = data.items.every((item: any) => item.isHomeDeliveryAvailable === true);
        return !allSupportCourier && !allSupportHomeDelivery;
    }, [data]);

    const handleViewOrderDetail = async (orderId: number) => {
        try {
            setIsLoading(true);
            const details = await orderServices.GetOrderByOrderId(orderId);
            if (details) {
                setDetailsOrder(details);
                setVisibleOrderDetail(true);
            } else {
                toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'No details found for this order' });
            }
        } catch (error: any) {
            console.error("Error while fetching order details:", error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to fetch order details' });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlaceOrder = async () => {
        if (!data) return;

        if (showMixedCartError) {
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Mixed Cart', 
                detail: 'Combination of shippable and non-shippable products cannot be delivered together.', 
                life: 4000 
            });
            return;
        }

        setIsLoading(true);
        try {
            // Ensure we have a valid address ID, taking both userAddressID and addressId into account
            // This aligns with how sales-ordering handles address selection
            const addressId = selectedAddress?.userAddressID || (selectedAddress as any)?.addressId || 0;

            const finalTotal = Math.max(0, Math.round(data.grandTotal - rewardsPointsRedeemed * rewardToCurrencyRatio));
            const isZeroTotal = finalTotal === 0;

            const finalData = {
                ...data,
                address: {
                    ...(selectedAddress || {}),
                    userAddressID: addressId
                } as any,
                userAddressID: addressId, // Some mappers might look for it at root
                courierProviderID: deliveryMethod?.id === 0 ? 0 : (deliveryMethod?.id || 0),
                comment: comment,
                // Add rewards information to finalData
                rewardPointsUsed: rewardsPointsRedeemed,
                rewardToCurrencyRatio: rewardToCurrencyRatio,
                rewardsPointsRedeemed: rewardsPointsRedeemed,
                rewardDiscountAmount: rewardsPointsRedeemed * rewardToCurrencyRatio,
                grandTotal: finalTotal, // Update total to the discounted one
                paymentOption: isZeroTotal ? { 
                    ...data.paymentOption, 
                    id: "10", 
                    mode: "Reward", 
                    code: "Reward", 
                    status: "Success",
                    description: "Paid by Rewards"
                } : data.paymentOption
            };

            const res = isAdmin ?
                await OrderService.setOrderForCustomer(finalData, Number(finalData.userId) || 0) :
                await OrderService.setOrderFromCustomer(finalData);

            if (res.isSuccess) {
                ProductStorageHelpers.ClearAllLocalStorage();
                window.dispatchEvent(new Event('cartUpdated'));
                dispatch(itemCount(0));
                dispatch(selectedPayment(null));
                setOrderId(res.id);
                setSuccessDetails({
                    amount: finalTotal,
                    date: Helpers.formatDateWithShortMonthSpaced(new Date()),
                    deliveryDate: res.additionalInfo?.expectedDeliveryDate || 'TBD'
                });

                if (["Cash On Delivery", "Payment Link", "Card", "UPI", "Reward"].includes(isZeroTotal ? "Reward" : data.paymentOption.mode)) {
                    setShowSuccess(true);
                } else {
                    dispatch(selectedGetway({ paymentURL: res.additionalInfo?.paymentURL, expectedDeliveryDate: res.additionalInfo?.expectedDeliveryDate }));
                    onOrderComplete ? onOrderComplete() : onClose();
                    navigate(RouteConstant.getwayProcess);
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Order placement failed' });
            }
        } catch (error: any) {
            console.error("Order error:", error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to place order' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!data) return <div className="p-4 text-center">Loading...</div>;

    return (
        <div className="flex flex-column h-full">
            <Toast ref={toast} />
            <div className="side-cart-header">
                <h5><i className="pi pi-check-circle text-xl" /> Confirm Order</h5>
                <div className="close-icon" onClick={onClose}>
                    <i className="pi pi-arrow-left" />
                </div>
            </div>

            <div className="side-cart-content text-sm">
                <div className="animate-in">
                    <span className="grey-label mb-3">Shipment & Payment Summary</span>

                    <div className="grid">
                        <div className="col-12">
                            <div className="summary-info-card address-detail-card" onClick={onChangeAddress}>
                                <div className="card-header">
                                    <i className="pi pi-map-marker" />
                                    <span>Delivery Address</span>
                                    <i className="pi pi-pencil ml-auto text-xs text-gray-400 cursor-pointer" />
                                </div>
                                <div className="card-body">
                                    <div className="primary-text">{selectedAddress?.userName}</div>
                                    <div className="text-gray-600">
                                        {selectedAddress?.address1}
                                        {selectedAddress?.address2 ? `, ${selectedAddress.address2}` : ''}
                                    </div>
                                    <div className="text-gray-600 text-xs mt-1">
                                        {selectedAddress?.areaName ? `${selectedAddress.areaName}, ` : ''}
                                        {selectedAddress?.cityName || (selectedAddress as any)?.city || ''},
                                        {selectedAddress?.stateName || (selectedAddress as any)?.state || ''} -
                                        {selectedAddress?.zipcode || (selectedAddress as any)?.pincode || ''}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 mb-2">
                            <div className="summary-info-card" onClick={onChangeDeliveryMethod}>
                                <div className="card-header">
                                    <i className="pi pi-truck" />
                                    <span>Delivery Method</span>
                                    <i className="pi pi-pencil ml-auto text-xs text-gray-400 cursor-pointer" />
                                </div>
                                <div className="card-body">
                                    <div className="primary-text">{deliveryMethod?.name}</div>
                                    <div className="text-xs">{deliveryMethod?.estimatedDays || 'Standard Delivery'}</div>
                                </div>
                            </div>
                        </div>

                        {!onNext && data.paymentOption?.mode && (
                            <div className="col-12 mb-2">
                                <div className="summary-info-card" onClick={onChangePaymentMethod}>
                                    <div className="card-header">
                                        <i className="pi pi-credit-card" />
                                        <span>Payment Method</span>
                                        <i className="pi pi-pencil ml-auto text-xs text-gray-400 cursor-pointer" />
                                    </div>
                                    <div className="card-body">
                                        <div className="primary-text">{data.paymentOption.mode}</div>
                                        <div className="text-xs">Secure Transaction</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-4">
                        <span className="grey-label mb-2">Order Items</span>
                        <div className="summary-item-list mb-3">
                            {data.items.map((item: any) => (
                                <div key={item.id} className="summary-item">
                                    <span className="summary-item-name" title={item.name}>
                                        {item.name} <span className="text-gray-400">×{item.quantity}</span>
                                    </span>
                                    <span className="summary-item-price">
                                        {Helpers.formatAmountInINR(item.unitPrice * item.quantity)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4">
                        <span className="grey-label mb-2">Order Totals</span>
                        <div className="bg-gray-50 border-round-xl p-3 border-1 surface-border">
                            <div className="summary-row mb-2">
                                <span className="text-gray-500">Cart Subtotal</span>
                                <span className="font-semibold">{Helpers.formatAmountInINR(data.totalItemCost)}</span>
                            </div>
                            <div className="summary-row mb-2">
                                <span className="text-gray-500">Delivery Charges</span>
                                <span className="font-semibold text-green-600">{data.courierCharges > 0 ? Helpers.formatAmountInINR(data.courierCharges) : 'FREE'}</span>
                            </div>
                            <div className="summary-row mb-2">
                                <span className="text-gray-500">Taxes</span>
                                <span className="font-semibold">{Helpers.formatAmountInINR(data.totalTax)}</span>
                            </div>
                            <div className="summary-total pt-3 mt-3 border-top-1 surface-border">
                                <span className="text-lg">Grand Total</span>
                                <div className="flex flex-column align-items-end">
                                    {useRewards && rewardsPointsRedeemed > 0 && (
                                        <span className="text-sm text-500 line-through">{Helpers.formatAmountInINR(data.grandTotal)}</span>
                                    )}
                                    <span className="amount text-2xl">
                                        {useRewards && rewardsPointsRedeemed > 0
                                            ? Helpers.formatAmountInINR(Math.max(0, Math.round(data.grandTotal - rewardsPointsRedeemed * rewardToCurrencyRatio)))
                                            : Helpers.formatAmountInINR(data.grandTotal)}
                                    </span>
                                    {useRewards && rewardsPointsRedeemed > 0 && (
                                        <span className="text-xs font-bold text-green-600">You saved {Helpers.formatAmountInINR(rewardsPointsRedeemed * rewardToCurrencyRatio)}!</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rewards Card */}
                    {availableRewardPoints > 0 && (
                        <div className="mt-3 p-3 border-round-lg border-1"
                            style={{ background: useRewards ? '#f0fdf4' : '#fafafa', borderColor: useRewards ? '#bbf7d0' : '#dee2e6' }}>
                            <div className="flex align-items-center justify-content-between mb-2">
                                <div className="flex align-items-center gap-2">
                                    <span className="text-lg">🎁</span>
                                    <div>
                                        <span className="font-bold text-800 text-xs uppercase block">Reward Points</span>
                                        {rewardToCurrencyRatio > 0 && (
                                            <span className="text-xs text-600">1 Point = {Helpers.formatAmountInINR(rewardToCurrencyRatio)}</span>
                                        )}
                                    </div>
                                </div>
                                <InputSwitch
                                    checked={useRewards}
                                    onChange={(e) => setUseRewards(e.value ?? false)}
                                    disabled={availableRewardPoints <= 0}
                                />
                            </div>
                            <div className="flex justify-content-between align-items-end">
                                <div>
                                    <p className="m-0 text-600 text-xs font-bold uppercase">Available</p>
                                    <p className="m-0 text-base font-bold text-900">{availableRewardPoints.toLocaleString()} <span className="text-xs text-500">pts</span></p>
                                </div>
                                <div className="text-right">
                                    <p className="m-0 text-600 text-xs font-bold uppercase">Redeeming</p>
                                    <p className={`m-0 text-base font-bold ${useRewards ? 'text-green-600' : 'text-400'}`}>
                                        {useRewards ? rewardsPointsRedeemed.toLocaleString() : '0'} <span className="text-xs text-500">pts</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}                    <div className="mt-4 p-3 bg-blue-50 border-round-lg text-blue-700 text-xs flex gap-2">
                        <i className="pi pi-info-circle text-base" />
                        <span>By placing this order, you agree to our terms of service and refund policy.</span>
                    </div>
                </div>
            </div>

            {(() => {
                const finalTotal = data ? Math.max(0, Math.round(data.grandTotal - rewardsPointsRedeemed * rewardToCurrencyRatio)) : 1;
                const isZeroTotal = finalTotal === 0;

                return (
                    <div className="drawer-footer" style={{ flexDirection: 'column', height: 'auto', gap: '1rem', padding: '1rem' }}>
                        {showMixedCartError && (
                            <div style={{ backgroundColor: '#ffcdd2', color: '#c62828', padding: '0.8rem', borderRadius: '12px', marginBottom: '0.5rem', fontSize: '0.85rem', display: 'flex', alignItems: 'start', border: '1px solid #ef9a9a', width: '100%' }}>
                                <i className="pi pi-times-circle mr-2" style={{ fontSize: '1.1rem', marginTop: '2px' }} />
                                <span>Combination of shippable and non-shippable products cannot be delivered together. Please create separate orders.</span>
                            </div>
                        )}
                        <div className="flex w-full gap-2">
                            <Button label="Back" outlined severity="secondary" className="btn-back" onClick={onBack} />
                            {isZeroTotal ? (
                                <Button
                                    label="Place Order Now"
                                    icon="pi pi-shopping-bag"
                                    iconPos="right"
                                    loading={isLoading}
                                    className="btn-continue checkout-btn"
                                    onClick={handlePlaceOrder}
                                    disabled={showMixedCartError}
                                />
                            ) : onNext ? (
                                <Button
                                    label="Continue"
                                    icon="pi pi-chevron-right"
                                    iconPos="right"
                                    className="btn-continue checkout-btn"
                                    onClick={() => {
                                        if (data) {
                                            const updatedData = {
                                                ...data,
                                                rewardPointsUsed: rewardsPointsRedeemed,
                                                rewardToCurrencyRatio: rewardToCurrencyRatio,
                                                rewardsPointsRedeemed: rewardsPointsRedeemed,
                                                rewardDiscountAmount: rewardsPointsRedeemed * rewardToCurrencyRatio,
                                                grandTotal: finalTotal,
                                            };
                                            ProductStorageHelpers.SetProcessedProductStorage(updatedData);
                                        }
                                        onNext();
                                    }}
                                />
                            ) : (
                                <Button
                                    label="Place Order"
                                    icon="pi pi-check-circle"
                                    iconPos="right"
                                    loading={isLoading}
                                    className="btn-continue checkout-btn"
                                    onClick={handlePlaceOrder}
                                    disabled={showMixedCartError}
                                />
                            )}
                        </div>
                    </div>
                );
            })()}

            <Dialog
                visible={showSuccess}
                onHide={() => { onOrderComplete ? onOrderComplete() : onClose(); navigate(RouteConstant.products); }}
                header="Order Successfully Placed"
                modal
                style={{ width: '400px' }}
                closable={true}
            >
                <div className="flex flex-column align-items-center text-center p-2">
                    <i className="pi pi-check-circle text-6xl text-green-500 mb-4" />
                    <div className="text-xl font-bold mb-2">Thank you for your order!</div>
                    <p className="text-600 mb-4">Your order has been placed successfully.</p>

                    <div className="w-full surface-100 p-3 border-round mb-4 text-left">
                        <div className="flex justify-content-between mb-2 align-items-center">
                            <span className="text-600">Order ID:</span>
                            <span
                                className="order-id-badge font-bold"
                                style={{ cursor: 'default' }}
                            >
                                {orderId}
                            </span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600">Date:</span>
                            <span className="font-bold">{successDetails.date}</span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600">Total Amount:</span>
                            <span className="font-bold text-green-600">{Helpers.formatAmountInINR(successDetails.amount)}</span>
                        </div>
                        <div className="flex justify-content-between">
                            <span className="text-600">Est. Delivery:</span>
                            <span className="font-bold">{Helpers.formatDateWithShortMonthSpaced(successDetails.deliveryDate)}</span>
                        </div>
                    </div>

                    <div className="w-full flex flex-column gap-2">
                        <Button label="View Order Details" icon="pi pi-copy" className="w-full checkout-btn" style={{ borderRadius: '2rem' }} onClick={() => { handleViewOrderDetail(orderId); }} />
                        <Button label="Home" outlined severity="secondary" icon="pi pi-home" className="w-full" style={{ borderRadius: '2rem' }} onClick={() => { onOrderComplete ? onOrderComplete() : onClose(); navigate(RouteConstant.products); }} />
                    </div>
                </div>
            </Dialog>

            {detailsOrder && (
                <Dialog
                    header="Order Details"
                    visible={visibleOrderDetail}
                    style={{ width: "90vw", maxWidth: "800px" }}
                    onHide={() => setVisibleOrderDetail(false)}
                    modal
                >
                    <TabView>
                        <TabPanel header="Order Details" leftIcon="pi pi-file mr-2">
                            <NewOrderReview
                                TriggerPrint={() => { }}
                                PrintEnabled={false}
                                OrderData={<OrderPrint orderinfo={detailsOrder} />}
                            />
                        </TabPanel>
                        <TabPanel header="Order Print" leftIcon="pi pi-print mr-2">
                            <OrderPrint orderinfo={detailsOrder} />
                        </TabPanel>
                    </TabView>
                </Dialog>
            )}
        </div>
    );
};

export default SidecartSummary;
